import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import bcrypt from 'bcryptjs';
import { ReactComponent as KaizIQText } from '@Assets/kaiziqText.svg';
import { ReactComponent as SecureIcon } from '@Assets/secure.svg';
import { ReactComponent as Boost } from '@Assets/boost.svg';
import { ReactComponent as MailIcon } from '@Assets/mailIcon.svg';
import OTPInput from 'otp-input-react';
import { MdEmail, MdLock, MdCheckCircle, MdArrowBack } from 'react-icons/md';
import { BiShow, BiHide } from 'react-icons/bi';
import { FiShield, FiMail, FiLock } from 'react-icons/fi';
import SignupService from '@Helpers/URLHelpers/SignupService';
import EmailVerificationService from '@Helpers/URLHelpers/AuthService/EmailVerificationService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import './ForgotPasswordPage.scss';

export default function ForgotPasswordPage({ setEntryPage, props }) {
    const className = 'forgotPasswordPage';

    // State management
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otp, setOtp] = useState('');
    const [currentStep, setCurrentStep] = useState(0); // 0: email, 1: otp, 2: password, 3: success

    // Validation states
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [emailNotFound, setEmailNotFound] = useState(false);
    const [invalidPassword, setInvalidPassword] = useState(false);
    const [invalidConfirmPassword, setInvalidConfirmPassword] = useState(false);
    const [invalidVerificationCode, setInvalidVerificationCode] = useState(false);

    // Loading states
    const [emailLoader, setEmailLoader] = useState(false);
    const [verifyLoader, setVerifyLoader] = useState(false);
    const [submitLoader, setSubmitLoader] = useState(false);

    // Timer states
    const [timerActive, setTimerActive] = useState(false);
    const [timerSeconds, setTimerSeconds] = useState(0);

    // Services
    const userAuthService = new UserAuthService();
    const signupService = new SignupService();
    const emailVerificationService = new EmailVerificationService();

    // Timer effect
    useEffect(() => {
        let interval = null;
        if (timerActive && timerSeconds > 0) {
            interval = setInterval(() => {
                setTimerSeconds(seconds => seconds - 1);
            }, 1000);
        } else if (timerSeconds === 0) {
            setTimerActive(false);
        }
        return () => clearInterval(interval);
    }, [timerActive, timerSeconds]);

    // Utility functions
    const emailValidCheck = (email) => {
        const validRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
        return email && email.match(validRegex);
    };

    const passwordStrengthCheck = (password) => {
        return password && password.length >= 8;
    };

    const emailTakenCheck = async (email) => {
        try {
            const user = await signupService.getUserByEmailForSignup(email);
            return user.body !== undefined;
        } catch (error) {
            return false;
        }
    };

    // Handlers
    const handleEmailChange = (e) => {
        const sanitizedEmail = DOMPurify.sanitize(e.target.value);
        setEmail(sanitizedEmail);
        setInvalidEmail(!emailValidCheck(sanitizedEmail));
        setEmailNotFound(false);
    };

    const handlePasswordChange = (e) => {
        const sanitizedPassword = DOMPurify.sanitize(e.target.value);
        const salt = '$2a$10$CwTycUXWue0Thq9StjUM0u';
        const hashedPassword = bcrypt.hashSync(sanitizedPassword, salt);
        setPassword(hashedPassword);
        setInvalidPassword(!passwordStrengthCheck(sanitizedPassword));
    };

    const handleConfirmPasswordChange = (e) => {
        const sanitizedConfirmPassword = DOMPurify.sanitize(e.target.value);
        const salt = '$2a$10$CwTycUXWue0Thq9StjUM0u';
        const hashedConfirmPassword = bcrypt.hashSync(sanitizedConfirmPassword, salt);
        setConfirmPassword(hashedConfirmPassword);
        setInvalidConfirmPassword(password !== hashedConfirmPassword);
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setEmailLoader(true);

        try {
            const emailExists = await emailTakenCheck(email);
            if (emailExists) {
                const emailVerification = await emailVerificationService.generateEmailVerificationCode(email);
                if (emailVerification.body === 'true') {
                    setCurrentStep(1);
                    setTimerSeconds(30);
                    setTimerActive(true);
                    setEmailNotFound(false);
                }
            } else {
                setEmailNotFound(true);
            }
        } catch (error) {
            props?.errorHandler?.();
        }

        setEmailLoader(false);
    };

    const handleVerifyOTP = async () => {
        setVerifyLoader(true);

        try {
            const otpBody = {
                toEmail: email,
                verificationCode: otp
            };
            const codeVerification = await emailVerificationService.verifyEmailVerificationCode(otpBody);
            const isValid = JSON.parse(codeVerification.body);

            if (isValid) {
                setCurrentStep(2);
                setInvalidVerificationCode(false);
            } else {
                setInvalidVerificationCode(true);
            }
        } catch (error) {
            setInvalidVerificationCode(true);
        }

        setVerifyLoader(false);
    };

    const handleSubmitPassword = async (e) => {
        e.preventDefault();
        setSubmitLoader(true);

        try {
            const body = {
                email: email,
                verificationCode: otp,
                password: password
            };
            const response = await userAuthService.forgotPasswordReset(body);
            const result = JSON.parse(response.body);

            if (result.message === 'Password Changed') {
                setCurrentStep(3);
                setTimeout(() => {
                    setEntryPage('login');
                }, 3000);
            } else {
                setInvalidVerificationCode(true);
            }
        } catch (error) {
            props?.errorHandler?.();
        }

        setSubmitLoader(false);
    };

    const handleResendOTP = async () => {
        if (!timerActive) {
            try {
                await emailVerificationService.generateEmailVerificationCode(email);
                setTimerSeconds(30);
                setTimerActive(true);
                setInvalidVerificationCode(false);
            } catch (error) {
                console.error('Failed to resend OTP');
            }
        }
    };

    const goBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            setEntryPage('login');
        }
    };

    const steps = [
        { icon: FiMail, title: 'Enter Email', description: 'We\'ll send you a verification code' },
        { icon: FiShield, title: 'Verify Code', description: 'Enter the 6-digit code from your email' },
        { icon: FiLock, title: 'New Password', description: 'Create a strong new password' },
        { icon: MdCheckCircle, title: 'Complete', description: 'Password successfully updated' }
    ];

    return (
        <div className={className}>
            {/* Background Elements */}
            <div className={className + '__background'}>
                <div className={className + '__background__gradient'}></div>
                <div className={className + '__background__pattern'}></div>
                <div className={className + '__background__shapes'}>
                    <div className={className + '__background__shape --shape1'}></div>
                    <div className={className + '__background__shape --shape2'}></div>
                    <div className={className + '__background__shape --shape3'}></div>
                </div>
            </div>

            {/* Main Container */}
            <div className={className + '__container'}>
                {/* Left Side - Branding */}
                <div className={className + '__branding'}>
                    <div className={className + '__branding__content'}>
                        <div className={className + '__branding__logo'}>
                            <KaizIQText className={className + '__branding__logoText'} />
                        </div>
                        <div className={className + '__branding__hero'}>
                            <h1 className={className + '__branding__title'}>
                                Secure Password Recovery
                            </h1>
                            <p className={className + '__branding__subtitle'}>
                                Don’t Worry, We’ve Got You Covered. Let's get you back into your account securely.
                            </p>
                        </div>
                        <div className={className + '__branding__features'}>
                            <div className={className + '__branding__feature'}>
                                <SecureIcon className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Secure Process</strong>
                                    <span>Multi-step verification to ensure your account’s safety</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature'}>
                                <Boost className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Quick Recovery</strong>
                                    <span>Reset your account access in just a few minutes</span>
                                </div>
                            </div>
                            <div className={className + '__branding__feature'}>
                                <MailIcon className={className + '__branding__featureIcon'} />
                                <div className={className + '__branding__featureText'}>
                                    <strong>Email Verification</strong>
                                    <span>A verification code will be sent to your registered email</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Reset Form */}
                <div className={className + '__formSection'}>
                    <div className={className + '__formContainer'}>
                        {/* Header */}
                        <div className={className + '__header'}>
                            <button className={className + '__backBtn flex-row-center-0p5rem-gap hovered'} onClick={goBack}>
                                <MdArrowBack />
                                Back
                            </button>
                            <h2 className={className + '__title'}>Reset Password</h2>
                            <p className={className + '__subtitle grey-text sm-text'}>
                                {steps[currentStep].description}
                            </p>
                        </div>

                        {/* Progress Steps */}
                        <div className={className + '__progress'}>
                            {steps.map((step, index) => {
                                const StepIcon = step.icon;
                                return (
                                    <div
                                        key={index}
                                        className={`${className}__progressStep ${
                                            index <= currentStep ? className + '__progressStep--active' : ''
                                        } ${index < currentStep ? className + '__progressStep--completed' : ''}`}
                                    >
                                        <div className={className + '__progressStep__icon'}>
                                            <StepIcon />
                                        </div>
                                        <span className={className + '__progressStep__label'}>
                                            {step.title}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Step Content */}
                        <div className={className + '__stepContent'}>
                            {/* Step 1: Email Input */}
                            {currentStep === 0 && (
                                <form onSubmit={handleSendOTP} className={className + '__form'}>
                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Email Address</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <MdEmail className={className + '__inputIcon'} />
                                            <input
                                                type="email"
                                                className={`${className}__input ${invalidEmail || emailNotFound ? className + '__input--error' : ''}`}
                                                placeholder="Enter your registered email"
                                                value={email}
                                                onChange={handleEmailChange}
                                                disabled={emailLoader}
                                            />
                                            {emailLoader && (
                                                <div className={className + '__inputLoader'}>
                                                    <div className={className + '__spinner'}></div>
                                                </div>
                                            )}
                                        </div>
                                        {invalidEmail && (
                                            <span className={className + '__fieldError'}>
                                                Please enter a valid email address
                                            </span>
                                        )}
                                        {emailNotFound && (
                                            <span className={className + '__fieldError'}>
                                                This email is not registered with us
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={!email || invalidEmail || emailLoader}
                                    >
                                        {emailLoader ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Sending...
                                            </>
                                        ) : 'Send Verification Code'}
                                    </button>
                                </form>
                            )}

                            {/* Step 2: OTP Verification */}
                            {currentStep === 1 && (
                                <div className={className + '__form'}>
                                    <div className={className + '__otpSection'}>
                                        <label className={className + '__label'}>Verification Code</label>
                                        <p className={className + '__otpDescription'}>
                                            Enter the 6-digit code sent to <strong>{email}</strong>
                                        </p>
                                        <div className={className + '__otpContainer'}>
                                            <OTPInput
                                                value={otp}
                                                onChange={setOtp}
                                                autoFocus
                                                OTPLength={6}
                                                otpType='number'
                                                disabled={verifyLoader}
                                                inputClass={className + '__otpInput'}
                                                className={className + '__otpWrapper'}
                                            />
                                        </div>
                                        <div className={className + '__otpFooter'}>
                                            {timerActive ? (
                                                <span className={className + '__timerText'}>
                                                    Resend code in {timerSeconds}s
                                                </span>
                                            ) : (
                                                <button
                                                    className={className + '__linkBtn'}
                                                    onClick={handleResendOTP}
                                                >
                                                    Resend code
                                                </button>
                                            )}
                                        </div>
                                        {invalidVerificationCode && (
                                            <span className={className + '__fieldError'}>
                                                Invalid verification code. Please try again.
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={className + '__primaryBtn'}
                                        onClick={handleVerifyOTP}
                                        disabled={otp.length !== 6 || verifyLoader}
                                    >
                                        {verifyLoader ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Verifying...
                                            </>
                                        ) : 'Verify Code'}
                                    </button>
                                </div>
                            )}

                            {/* Step 3: New Password */}
                            {currentStep === 2 && (
                                <form onSubmit={handleSubmitPassword} className={className + '__form'}>
                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>New Password</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <MdLock className={className + '__inputIcon'} />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className={`${className}__input ${invalidPassword ? className + '__input--error' : ''}`}
                                                placeholder="Enter new password"
                                                onChange={handlePasswordChange}
                                                disabled={submitLoader}
                                            />
                                            <button
                                                type="button"
                                                className={className + '__togglePassword'}
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <BiHide /> : <BiShow />}
                                            </button>
                                        </div>
                                        {invalidPassword && (
                                            <span className={className + '__fieldError'}>
                                                Password must be at least 8 characters
                                            </span>
                                        )}
                                    </div>

                                    <div className={className + '__inputGroup'}>
                                        <label className={className + '__label grey-text sm-text'}>Confirm Password</label>
                                        <div className={className + '__inputWrapper flex-row-center'}>
                                            <MdLock className={className + '__inputIcon'} />
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                className={`${className}__input ${invalidConfirmPassword ? className + '__input--error' : ''}`}
                                                placeholder="Confirm new password"
                                                onChange={handleConfirmPasswordChange}
                                                disabled={submitLoader}
                                            />
                                            <button
                                                type="button"
                                                className={className + '__togglePassword'}
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <BiHide /> : <BiShow />}
                                            </button>
                                        </div>
                                        {invalidConfirmPassword && (
                                            <span className={className + '__fieldError'}>
                                                Passwords don't match
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className={className + '__primaryBtn'}
                                        disabled={!password || !confirmPassword || invalidPassword || invalidConfirmPassword || password !== confirmPassword || submitLoader}
                                    >
                                        {submitLoader ? (
                                            <>
                                                <div className={className + '__spinner'}></div>
                                                Updating...
                                            </>
                                        ) : 'Update Password'}
                                    </button>
                                </form>
                            )}

                            {/* Step 4: Success */}
                            {currentStep === 3 && (
                                <div className={className + '__successView'}>
                                    <div className={className + '__successIcon'}>
                                        <MdCheckCircle />
                                    </div>
                                    <h3 className={className + '__successTitle'}>Password Updated!</h3>
                                    <p className={className + '__successMessage'}>
                                        Your password has been successfully updated. You can now log in with your new password.
                                    </p>
                                    <div className={className + '__successRedirect'}>
                                        Redirecting to login page in 3 seconds...
                                    </div>
                                    <button
                                        className={className + '__primaryBtn'}
                                        onClick={() => setEntryPage('login')}
                                    >
                                        Go to Login
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className={className + '__footer flex-row-center-1rem-gap sm-text'}>
                            <div className={className + '__footer__t1 grey-text'}>
                                Remember your password?
                            </div>
                            <button className={className + '__linkBtn sm-text hovered'} onClick={() => setEntryPage('login')}>
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
